import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ArrowLeft, Save, Users, Pencil } from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { CompactTable } from "../../components/ui/CompactTable";
import { useConfirm } from '../../hooks/use-confirm';
import { legacyApi, LegacySupplier } from '../../services/legacyApi';
import { toast } from 'sonner';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';

type SupplierForm = Omit<LegacySupplier, 'supplier_id'> & { supplier_id?: number };

const emptySupplier: SupplierForm = {
    supplier_name: '',
    poc_name: '',
    poc_contact: '',
    emirate: '',
    office_tel: '',
    email_address: '',
    trn: '',
    address: ''
};

export function SupplierManager() {
    const [suppliers, setSuppliers] = useState<LegacySupplier[]>([]);
    const [loading, setLoading] = useState(false);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState<SupplierForm>(emptySupplier);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const { confirm } = useConfirm();

    const fetchSuppliers = async () => {
        setLoading(true);
        try {
            const data = await legacyApi.suppliers.list();
            setSuppliers(data);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load suppliers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
    }, []);

    const startCreate = () => {
        setForm(emptySupplier);
        setFieldErrors({});
        setIsEditing(true);
    };

    const startEdit = (supplier: LegacySupplier) => {
        setForm({ ...supplier });
        setFieldErrors({});
        setIsEditing(true);
    };

    const handleSave = async () => {
        try {
            const validation = validateRequiredFields([
                { key: 'supplier_name', label: 'Supplier Name', value: form.supplier_name, required: true },
            ]);
            if (!validation.valid) {
                setFieldErrors(validation.errorsByField);
                showValidationSummaryToast();
                focusFirstInvalidField(validation.firstInvalidField);
                return;
            }
            setFieldErrors({});

            const payload = {
                supplier_name: form.supplier_name,
                poc_name: form.poc_name || undefined,
                poc_contact: form.poc_contact || undefined,
                emirate: form.emirate || undefined,
                office_tel: form.office_tel || undefined,
                email_address: form.email_address || undefined,
                trn: form.trn || undefined,
                address: form.address || undefined
            };

            if (form.supplier_id) {
                await legacyApi.suppliers.update(form.supplier_id, payload);
            } else {
                await legacyApi.suppliers.create(payload);
            }

            setIsEditing(false);
            setForm(emptySupplier);
            fetchSuppliers();
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || "Failed to save supplier");
        }
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm('Delete this supplier?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await legacyApi.suppliers.delete(id);
            toast.success('Supplier deleted');
            fetchSuppliers();
        } catch (e: any) {
            console.error(e);
            toast.error(e.message || "Failed to delete supplier");
        }
    };

    const filtered = useMemo(() => suppliers.filter(s => {
        const query = state.q.toLowerCase();
        const haystack = [
            s.supplier_name,
            s.poc_name,
            s.poc_contact,
            s.emirate,
            s.office_tel,
            s.email_address,
            s.trn,
            s.address
        ].join(' ').toLowerCase();
        return haystack.includes(query);
    }), [suppliers, state.q]);
    // Column definitions
    const columns = [
        { header: 'ID', accessor: (s: LegacySupplier) => `S-${String(s.supplier_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
        { header: 'Supplier', accessor: (s: LegacySupplier) => s.supplier_name, className: 'font-medium text-slate-700' },
        { header: 'Contact', accessor: (s: LegacySupplier) => s.poc_name || '-', className: 'text-slate-600' },
        { header: 'Phone', accessor: (s: LegacySupplier) => s.poc_contact || s.office_tel || '-', className: 'text-slate-600' },
        { header: 'Email', accessor: (s: LegacySupplier) => s.email_address || '-', className: 'text-slate-600' },
        { header: 'Emirate', accessor: (s: LegacySupplier) => s.emirate || '-', className: 'text-slate-600' },
        { header: 'Actions', accessor: (s: LegacySupplier) => (
            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => startEdit(s)}
                >
                    <Pencil className="w-4 h-4 text-blue-600" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(s.supplier_id)}
                >
                    <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
            </div>
        ), className: 'text-right' }
    ];
    if (isEditing) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => { setIsEditing(false); setForm(emptySupplier); }}>
                        <ArrowLeft className="w-5 h-5 text-slate-500" />
                    </Button>
                    <h2 className="text-xl font-semibold text-slate-800">{form.supplier_id ? "Edit Supplier" : "New Supplier"}</h2>
                </div>

                <Card className="max-w-3xl">
                    <CardHeader>
                        <CardTitle>Supplier Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2 md:col-span-2">
                                <RequiredLabel htmlFor="supplier_name" required>Supplier Name</RequiredLabel>
                                <Input
                                    id="supplier_name"
                                    name="supplier_name"
                                    aria-invalid={!!fieldErrors.supplier_name}
                                    aria-required="true"
                                    value={form.supplier_name}
                                    onChange={e => setForm({ ...form, supplier_name: e.target.value })}
                                />
                                <FieldErrorText message={fieldErrors.supplier_name} />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Contact Person</label>
                                <Input
                                    value={form.poc_name || ''}
                                    onChange={e => setForm({ ...form, poc_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Contact Number</label>
                                <Input
                                    value={form.poc_contact || ''}
                                    onChange={e => setForm({ ...form, poc_contact: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Office Tel</label>
                                <Input
                                    value={form.office_tel || ''}
                                    onChange={e => setForm({ ...form, office_tel: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Email</label>
                                <Input
                                    value={form.email_address || ''}
                                    onChange={e => setForm({ ...form, email_address: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Emirate</label>
                                <Input
                                    value={form.emirate || ''}
                                    onChange={e => setForm({ ...form, emirate: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">TRN</label>
                                <Input
                                    value={form.trn || ''}
                                    onChange={e => setForm({ ...form, trn: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <label className="text-sm font-medium">Address</label>
                                <Input
                                    value={form.address || ''}
                                    onChange={e => setForm({ ...form, address: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <Button onClick={handleSave} disabled={!form.supplier_name}>
                                <Save className="w-4 h-4 mr-2" /> Save Supplier
                            </Button>
                            <Button variant="outline" onClick={() => { setIsEditing(false); setForm(emptySupplier); }}>
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Controls */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50/50">
                <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-lg font-semibold">Suppliers</span>
                </div>
            </div>
            <div className="p-4 border-b">
                <TableFilterBar
                    searchValue={state.q}
                    onSearchChange={(value) => setFilter('q', value)}
                    searchPlaceholder="Search suppliers by name, contact, TRN, emirate..."
                    isDirty={isDirty}
                    onClear={clearAll}
                    rightSlot={(
                        <div className="flex items-center gap-2">
                            <span>{filtered.length} results</span>
                            <Button onClick={startCreate}>
                                <Plus className="w-4 h-4 mr-1" /> Add Supplier
                            </Button>
                        </div>
                    )}
                />
            </div>

            <div className="flex-1 overflow-auto">
                <CompactTable
                    data={filtered}
                    columns={columns}
                    keyField="supplier_id"
                    loading={loading}
                    emptyMessage="No suppliers found"
                />
            </div>
        </div>
    );
}
